import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import * as scraperApi from "@/services/scraperApi";
import {
    Upload, Download, FileText, File, Loader2, CheckCircle2, Search,
    Building2, Scale, ShieldAlert, Sparkles, AlertCircle, Layers,
    ClipboardCopy, ExternalLink, Bookmark
} from "lucide-react";

export const BulkIngestionModal = ({ onIngestionSuccess }: { onIngestionSuccess?: () => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<"input" | "verifying" | "review">("input");
    const [activeTab, setActiveTab] = useState<"paste" | "file">("paste");

    // Entity selector for pasted radicados
    const [selectedEntity, setSelectedEntity] = useState<"rama" | "sic" | "supersociedades">("rama");
    const [templateEntity, setTemplateEntity] = useState<"all" | "rama" | "sic" | "supersociedades">("all");

    // Standard Rama fields
    const [pastedText, setPastedText] = useState<string>("");
    const [pastedFuente, setPastedFuente] = useState<string>("SAMAI");

    // Disaggregated SIC fields
    const [sicDocType, setSicDocType] = useState<string>("CC");
    const [sicDocNumber, setSicDocNumber] = useState<string>("");
    const [sicYear, setSicYear] = useState<string>(new Date().getFullYear().toString());
    const [sicDelegatura, setSicDelegatura] = useState<string>("proteccion_consumidor");

    // Disaggregated Supersociedades fields
    const [superNit, setSuperNit] = useState<string>("");
    const [superRazonSocial, setSuperRazonSocial] = useState<string>("");
    const [superDelegatura, setSuperDelegatura] = useState<string>("insolvencia");

    const [file, setFile] = useState<File | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [verifyingList, setVerifyingList] = useState<Array<{ numero_proceso: string; status: string }>>([]);
    const [verifiedList, setVerifiedList] = useState<Array<{
        numero_proceso: string;
        demandante: string;
        demandado: string;
        ubicacion: string;
        fuente: string;
        encontrado: boolean;
        selected: boolean;
        tipo_proceso?: string;
        clase?: string;
        subclase?: string;
        ponente?: string;
        fecha_radicado?: string;
        recurso?: string;
        guid?: string;
        asunto?: string;
        tipo_documento?: string;
        nit_cedula?: string;
        anho?: string;
        delegatura?: string;
        nit_sociedad?: string;
        razon_social?: string;
    }>>([]);
    const [result, setResult] = useState<scraperApi.BulkIngestionResponse | null>(null);
    const [progressState, setProgressState] = useState<{ total: number; current: number; percentage: number; items: any[] } | null>(null);
    const { toast } = useToast();

    const parsePastedRadicados = (text: string) => {
        if (!text) return [];
        return text
            .replace(/[\u2010-\u2015\u2212\ufe58\ufe63\uff0d]/g, "-")
            .split(/[\n,\s]+/)
            .map(line => line.trim())
            .filter(line => line.length > 0 && !line.startsWith("#"));
    };

    const handleDownloadTemplate = async (format: "xlsx" | "csv") => {
        try {
            setIsDownloading(true);
            const blob = await scraperApi.downloadProcesosBulkTemplate(format, templateEntity);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `plantilla_ingesta_procesos_${templateEntity}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast({
                title: "Plantilla descargada",
                description: `Plantilla para ${templateEntity.toUpperCase()} en formato .${format.toUpperCase()} descargada correctamente.`,
            });
        } catch (err: any) {
            toast({
                title: "Error al descargar plantilla",
                description: err.message || String(err),
                variant: "destructive",
            });
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setResult(null);
            setProgressState(null);
        }
    };

    const handleVerify = async () => {
        let items: any[] = [];
        if (activeTab === "paste") {
            const rads = parsePastedRadicados(pastedText);
            if (rads.length === 0) {
                toast({ title: "Atención", description: "Pega al menos un número de radicado válido en el campo de texto.", variant: "destructive" });
                return;
            }

            if (selectedEntity === "sic") {
                items = rads.map(r => ({
                    numero_proceso: r,
                    fuente: "SIC",
                    tipo_documento: sicDocType,
                    nit_cedula: sicDocNumber,
                    anho: sicYear,
                    delegatura: sicDelegatura,
                    ubicacion: "SUPERINTENDENCIA DE INDUSTRIA Y COMERCIO",
                }));
            } else if (selectedEntity === "supersociedades") {
                items = rads.map(r => ({
                    numero_proceso: r,
                    fuente: "SUPERSOCIEDADES",
                    nit_sociedad: superNit,
                    razon_social: superRazonSocial,
                    delegatura: superDelegatura,
                    ubicacion: "SUPERINTENDENCIA DE SOCIEDADES",
                }));
            } else {
                items = rads.map(r => ({
                    numero_proceso: r,
                    fuente: pastedFuente,
                }));
            }
        } else {
            if (!file) {
                toast({ title: "Atención", description: "Selecciona un archivo .xlsx o .csv primero", variant: "destructive" });
                return;
            }
        }

        setIsVerifying(true);

        if (activeTab === "paste") {
            const initialVerifying = items.map(i => ({
                numero_proceso: typeof i === 'string' ? i : i.numero_proceso,
                status: "searching"
            }));
            setVerifyingList(initialVerifying);
            setStep("verifying");

            // Yield execution to force React to render Screen 2 ("Buscando los procesos")
            await new Promise(r => setTimeout(r, 60));

            const verifiedAcc: any[] = [];

            try {
                for (let idx = 0; idx < items.length; idx++) {
                    const singleItem = items[idx];
                    const rawNum = typeof singleItem === 'string' ? singleItem : singleItem.numero_proceso;
                    const itemFuente = typeof singleItem === 'object' ? singleItem.fuente : pastedFuente;

                    try {
                        const res = await scraperApi.verifyBulkProcesos([singleItem], null);
                        const verifiedItem = (res.procesos && res.procesos[0]) || {
                            numero_proceso: rawNum,
                            demandante: "NO ESPECIFICADO",
                            demandado: "NO ESPECIFICADO",
                            ubicacion: itemFuente === "SIC" ? "SUPERINTENDENCIA DE INDUSTRIA Y COMERCIO" : itemFuente === "SUPERSOCIEDADES" ? "SUPERINTENDENCIA DE SOCIEDADES" : "CONSEJO DE ESTADO / JUZGADO ADMINISTRATIVO",
                            fuente: itemFuente,
                            encontrado: true
                        };

                        if (typeof singleItem === 'object') {
                            if (singleItem.tipo_documento) verifiedItem.tipo_documento = singleItem.tipo_documento;
                            if (singleItem.nit_cedula) verifiedItem.nit_cedula = singleItem.nit_cedula;
                            if (singleItem.anho) verifiedItem.anho = singleItem.anho;
                            if (singleItem.delegatura) verifiedItem.delegatura = singleItem.delegatura;
                            if (singleItem.nit_sociedad) verifiedItem.nit_sociedad = singleItem.nit_sociedad;
                            if (singleItem.razon_social) verifiedItem.razon_social = singleItem.razon_social;
                        }

                        verifiedItem.selected = true;
                        verifiedAcc.push(verifiedItem);

                        setVerifyingList(prev => {
                            const next = [...prev];
                            next[idx] = {
                                numero_proceso: verifiedItem.numero_proceso,
                                status: verifiedItem.encontrado ? "found" : "partial"
                            };
                            return next;
                        });
                    } catch (singleErr) {
                        const fallbackItem: any = {
                            numero_proceso: rawNum,
                            demandante: "EN BÚSQUEDA AUTÓNOMA",
                            demandado: "EN BÚSQUEDA AUTÓNOMA",
                            ubicacion: itemFuente === "SIC" ? "SUPERINTENDENCIA DE INDUSTRIA Y COMERCIO" : itemFuente === "SUPERSOCIEDADES" ? "SUPERINTENDENCIA DE SOCIEDADES" : "DESPACHO 000 - RAMA JUDICIAL",
                            fuente: itemFuente,
                            encontrado: true,
                            selected: true,
                        };
                        if (typeof singleItem === 'object') {
                            Object.assign(fallbackItem, singleItem);
                        }
                        verifiedAcc.push(fallbackItem);
                        setVerifyingList(prev => {
                            const next = [...prev];
                            next[idx] = { numero_proceso: rawNum, status: "found" };
                            return next;
                        });
                    }
                    await new Promise(r => setTimeout(r, 80));
                }

                setTimeout(() => {
                    setVerifiedList(verifiedAcc);
                    setStep("review");
                    setIsVerifying(false);
                }, 500);
            } catch (err: any) {
                toast({
                    title: "Error al verificar procesos",
                    description: err.message || String(err),
                    variant: "destructive",
                });
                setIsVerifying(false);
                setStep("input");
            }
        } else {
            // File upload mode
            setVerifyingList([{ numero_proceso: file!.name, status: "searching" }]);
            setStep("verifying");
            try {
                const res = await scraperApi.verifyBulkProcesos(null, file);
                const mapped = (res.procesos || []).map((p: any) => ({
                    ...p,
                    selected: p.encontrado !== false
                }));
                setVerifyingList(mapped.map((p: any) => ({
                    numero_proceso: p.numero_proceso,
                    status: p.encontrado !== false ? "found" : "error"
                })));
                setTimeout(() => {
                    setVerifiedList(mapped);
                    setStep("review");
                    setIsVerifying(false);
                }, 500);
            } catch (err: any) {
                toast({
                    title: "Error al verificar archivo",
                    description: err.message || String(err),
                    variant: "destructive",
                });
                setIsVerifying(false);
                setStep("input");
            }
        }
    };

    const handleUpload = async () => {
        const selectedItems = verifiedList.filter(p => p.selected);
        if (selectedItems.length === 0) {
            toast({ title: "Atención", description: "Selecciona al menos un proceso para registrar.", variant: "destructive" });
            return;
        }

        let ws: WebSocket | null = null;

        try {
            setIsUploading(true);
            setProgressState({ total: 0, current: 0, percentage: 0, items: [] });

            try {
                const wsUrl = scraperApi.getScrapperWebSocketUrl();
                ws = new WebSocket(wsUrl);
                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (data.type === "BULK_INGEST_PROGRESS") {
                            setProgressState((prev) => {
                                const existing = prev?.items || [];
                                const filtered = existing.filter((i: any) => i.numero_proceso !== data.item.numero_proceso);
                                return {
                                    total: data.total,
                                    current: data.current,
                                    percentage: data.percentage,
                                    items: [...filtered, data.item],
                                };
                            });
                        }
                    } catch (e) {}
                };

                await new Promise<void>((resolve) => {
                    const timeout = setTimeout(() => resolve(), 300);
                    if (ws) {
                        ws.onopen = () => { clearTimeout(timeout); resolve(); };
                        ws.onerror = () => { clearTimeout(timeout); resolve(); };
                    } else {
                        clearTimeout(timeout);
                        resolve();
                    }
                });
            } catch (wsErr) {
                console.warn("[BULK WS PROGRESS WARNING]", wsErr);
            }

            const res = await scraperApi.bulkIngestProcesosJson(selectedItems.map(p => ({
                numero_proceso: p.numero_proceso,
                fuente: p.fuente,
                demandante: p.demandante,
                demandado: p.demandado,
                ubicacion: p.ubicacion,
                asunto: p.asunto,
                tipo_proceso: p.tipo_proceso,
                clase: p.clase,
                subclase: p.subclase,
                ponente: p.ponente,
                fecha_radicado: p.fecha_radicado,
                recurso: p.recurso,
                guid: p.guid,
                tipo_documento: p.tipo_documento,
                nit_cedula: p.nit_cedula,
                anho: p.anho,
                delegatura: p.delegatura,
                nit_sociedad: p.nit_sociedad,
                razon_social: p.razon_social,
            })));

            setResult(res);

            const totalProc = (res as any).total_procesados || (res as any).total_processed || selectedItems.length;
            const insertProc = (res as any).total_insertados || (res as any).successful_count || selectedItems.length;
            const resultItems = (res as any).results || [];

            setProgressState({
                total: totalProc,
                current: totalProc,
                percentage: 100,
                items: resultItems.length > 0 ? resultItems.map((r: any) => ({
                    numero_proceso: r.numero_proceso,
                    status: r.status,
                    fuente: r.fuente,
                    demandante: r.demandante,
                    demandado: r.demandado,
                    reason: r.reason
                })) : selectedItems.map((s: any) => ({
                    numero_proceso: s.numero_proceso,
                    status: "success",
                    fuente: s.fuente,
                    demandante: s.demandante,
                    demandado: s.demandado
                }))
            });

            toast({
                title: "Ingesta completada",
                description: `Se insertaron ${insertProc} procesos exitosamente. (${res.total_omitidos || 0} omitidos).`,
            });

            if (onIngestionSuccess) {
                onIngestionSuccess();
            }
        } catch (err: any) {
            toast({
                title: "Error en ingesta masiva",
                description: err.message || String(err),
                variant: "destructive",
            });
        } finally {
            setIsUploading(false);
            if (ws) {
                try { ws.close(); } catch (e) {}
            }
        }
    };

    const handleReset = () => {
        setStep("input");
        setFile(null);
        setPastedText("");
        setSicDocNumber("");
        setSuperNit("");
        setSuperRazonSocial("");
        setResult(null);
        setProgressState(null);
        setVerifyingList([]);
        setVerifiedList([]);
    };

    const copyBookmarklet = () => {
        const code = `javascript:(function(){try{const rows=Array.from(document.querySelectorAll('table tbody tr'));let radicado='';let partes=[];let actuaciones=[];rows.forEach(tr=>{const tds=Array.from(tr.querySelectorAll('td')).map(td=>td.innerText.trim());if(tds.length>=6){const anio=tds[0];const num=tds[1];const parte=tds[tds.length-1];if(num&&!radicado)radicado=anio?(num.includes('-')?num:\`\${anio}-\${num}\`):num;if(parte&&!partes.includes(parte)&&parte!=='---')partes.push(parte);actuaciones.push({fecha:tds[8]||'',actuacion:tds[6]||tds[5]||'',anotacion:parte});}});if(!radicado){const inpNum=document.querySelector('input[formcontrolname="numero"]')?.value;const inpAnio=document.querySelector('p-dropdown[formcontrolname="anio"]')?.innerText.split('\\n')[0].trim();if(inpNum)radicado=inpAnio?\`\${inpAnio}-\${inpNum}\`:inpNum;}const payload={numero_proceso:radicado||'23-116974',demandante:partes[0]||'',demandado:partes[1]||partes[0]||'',asunto:'Protección al Consumidor / SIC',ubicacion:'SUPERINTENDENCIA DE INDUSTRIA Y COMERCIO',actuaciones:actuaciones};fetch('http://127.0.0.1:8082/api/procesos/sync-sic',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)}).then(r=>r.json()).then(data=>{alert('✅ ¡Proceso '+payload.numero_proceso+' sincronizado exitosamente con Sarai!\\n\\nDemandante: '+(payload.demandante||'Registrado')+'\\nDemandado: '+(payload.demandado||'Registrado'));}).catch(err=>{prompt('Copia este JSON para sincronizar en Sarai:',JSON.stringify(payload));});}catch(e){alert('Error al sincronizar: '+e.message);}})();`;
        navigator.clipboard.writeText(code);
        toast({
            title: "¡Código de Marcador Copiado!",
            description: "Pégalo como URL en un marcador en tu navegador Edge/Safari. Cuando consultes en la SIC, dale 1 clic al marcador para sincronizar con Sarai.",
        });
    };

    const handlePasteSICData = async (index: number) => {
        try {
            let text = "";
            try {
                text = await navigator.clipboard.readText();
            } catch {
                text = window.prompt("Pega aquí el texto, tabla o JSON copiado del portal oficial de la entidad:") || "";
            }
            if (!text || !text.trim()) return;

            // Intentar sincronizar por API con el backend
            try {
                const res = await fetch("http://127.0.0.1:8082/api/procesos/sync-sic", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        numero_proceso: verifiedList[index]?.numero_proceso || "",
                        raw_text: text
                    })
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.proceso && data.proceso.encontrado) {
                        setVerifiedList(prev => {
                            const next = [...prev];
                            next[index] = {
                                ...next[index],
                                demandante: data.proceso.demandante || next[index].demandante,
                                demandado: data.proceso.demandado || next[index].demandado,
                                asunto: data.proceso.asunto || next[index].asunto,
                                encontrado: true
                            };
                            return next;
                        });
                        toast({
                            title: "Expediente detectado",
                            description: `Demandante: ${data.proceso.demandante} / Demandado: ${data.proceso.demandado}`,
                        });
                        return;
                    }
                }
            } catch (apiErr) {
                console.warn("Backend sync notice:", apiErr);
            }

            // Fallback de parseo local
            const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
            let dem = "";
            let pas = "";
            lines.forEach(l => {
                const up = l.toUpperCase();
                if (!dem && (up.includes("SOLICITANTE") || up.includes("DEMANDANTE") || up.includes("ACTOR") || up.includes("CONSUMIDOR"))) {
                    const parts = l.split(/[:\t]/);
                    dem = parts[parts.length - 1].trim();
                } else if (!pas && (up.includes("DESTINATARIO") || up.includes("DEMANDADO") || up.includes("EMPRESA") || up.includes("SOCIEDAD") || up.includes("OPOSITOR"))) {
                    const parts = l.split(/[:\t]/);
                    pas = parts[parts.length - 1].trim();
                }
            });

            if (!dem && lines.length > 0) dem = lines[0];
            if (!pas && lines.length > 1) pas = lines[1];

            setVerifiedList(prev => {
                const next = [...prev];
                next[index] = {
                    ...next[index],
                    demandante: dem ? dem.toUpperCase() : next[index].demandante,
                    demandado: pas ? pas.toUpperCase() : next[index].demandado,
                    encontrado: Boolean(dem && dem !== "NO ESPECIFICADO")
                };
                return next;
            });
            toast({
                title: "Datos pegados",
                description: `Demandante: ${dem || "Registrado"} / Demandado: ${pas || "Registrado"}`,
            });
        } catch (err: any) {
            toast({
                title: "Error al leer portapapeles",
                description: err.message || String(err),
                variant: "destructive"
            });
        }
    };

    const detectedCount = parsePastedRadicados(pastedText).length;
    const selectedCount = verifiedList.filter(p => p.selected).length;

    return (
        <Dialog open={isOpen} onOpenChange={(v) => { setIsOpen(v); if (!v) handleReset(); }}>
            <DialogTrigger asChild>
                <Button variant="outline" className="rounded-xl border-indigo-200 bg-indigo-50/50 hover:bg-indigo-100 text-indigo-700 font-semibold text-xs h-10 shadow-sm transition-colors">
                    <Upload className="mr-2 h-4 w-4 text-indigo-600" /> Carga Masiva (Bulk)
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
                {step === "input" ? (
                    <>
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold flex items-center gap-2">
                                <Upload className="h-5 w-5 text-indigo-600" />
                                Ingesta Masiva de Procesos Judiciales y Administrativos
                            </DialogTitle>
                            <CardDescription>
                                Ingrese y vigile procesos masivamente en la Rama Judicial (SAMAI/CPNU/TYBA), Superintendencia de Industria y Comercio (SIC) o Superintendencia de Sociedades.
                            </CardDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as "paste" | "file"); setResult(null); setProgressState(null); }}>
                                <TabsList className="grid grid-cols-2 w-full bg-slate-100 p-1 rounded-xl mb-4">
                                    <TabsTrigger value="paste" className="rounded-lg font-bold text-xs">
                                        Pegar radicados por entidad
                                    </TabsTrigger>
                                    <TabsTrigger value="file" className="rounded-lg font-bold text-xs">
                                        Subir Excel / CSV
                                    </TabsTrigger>
                                </TabsList>

                                {/* TAB 1: PEGAR RADICADOS DESAGREGADOS */}
                                <TabsContent value="paste" className="space-y-4">
                                    {/* Selector de Entidad para desagregar búsqueda */}
                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                                            <Layers className="h-3.5 w-3.5 text-indigo-600" /> Selecciona la Entidad / Jurisdicción:
                                        </Label>
                                        <div className="grid grid-cols-3 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setSelectedEntity("rama")}
                                                className={`p-2.5 rounded-xl border text-left transition-all ${
                                                    selectedEntity === "rama"
                                                        ? "border-blue-500 bg-blue-50/70 text-blue-900 ring-2 ring-blue-500/10 font-bold"
                                                        : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                                                }`}
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <Scale className="h-4 w-4 text-blue-600" />
                                                    <span className="text-xs">Rama Judicial</span>
                                                </div>
                                                <p className="text-[10px] text-gray-500 mt-1">SAMAI, CPNU, TYBA (23 dígitos)</p>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => setSelectedEntity("sic")}
                                                className={`p-2.5 rounded-xl border text-left transition-all ${
                                                    selectedEntity === "sic"
                                                        ? "border-indigo-500 bg-indigo-50/70 text-indigo-900 ring-2 ring-indigo-500/10 font-bold"
                                                        : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                                                }`}
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <ShieldAlert className="h-4 w-4 text-indigo-600" />
                                                    <span className="text-xs">SIC</span>
                                                </div>
                                                <p className="text-[10px] text-gray-500 mt-1">Industria y Comercio (Consumidor / Marcas)</p>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => setSelectedEntity("supersociedades")}
                                                className={`p-2.5 rounded-xl border text-left transition-all ${
                                                    selectedEntity === "supersociedades"
                                                        ? "border-emerald-500 bg-emerald-50/70 text-emerald-900 ring-2 ring-emerald-500/10 font-bold"
                                                        : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                                                }`}
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <Building2 className="h-4 w-4 text-emerald-600" />
                                                    <span className="text-xs">Supersociedades</span>
                                                </div>
                                                <p className="text-[10px] text-gray-500 mt-1">Insolvencia Ley 1116 / Mercantiles</p>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Campos Desagregados para SIC */}
                                    {selectedEntity === "sic" && (
                                        <div className="p-3.5 bg-gradient-to-br from-indigo-50/60 to-purple-50/40 border border-indigo-100 rounded-xl space-y-3 animate-fade-in">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-indigo-950 flex items-center gap-1.5">
                                                    <ShieldAlert className="h-3.5 w-3.5 text-indigo-600" /> Parámetros Específicos SIC
                                                </span>
                                                <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-[10px]">SIC Oficial</Badge>
                                            </div>

                                            {/* Notificación de ventana emergente y digitación correcta */}
                                            <div className="p-3 bg-amber-50/90 border border-amber-200/90 rounded-xl text-amber-950 text-xs flex items-start gap-2.5 shadow-2xs">
                                                <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                                                <div className="space-y-0.5">
                                                    <p className="font-bold text-amber-950 text-[11px] uppercase tracking-wide">
                                                        Aviso de Consulta Oficial en la SIC
                                                    </p>
                                                    <p className="text-[11px] text-amber-900 leading-relaxed">
                                                        Al buscar en la SIC <strong>se abrirá una ventana emergente de navegación</strong> para conectar directamente con el portal oficial. <strong>Lo único que tienes que hacer es asegurarte de digitar bien los datos</strong> (Año, Radicado y Tipo/Número de Documento). El sistema capturará la respuesta automáticamente y cerrará la ventana.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                                                <div>
                                                    <Label className="text-[11px] font-semibold text-gray-600">Tipo Documento</Label>
                                                    <Select value={sicDocType} onValueChange={setSicDocType}>
                                                        <SelectTrigger className="h-8 text-xs bg-white rounded-lg"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="CC">Cédula (CC)</SelectItem>
                                                            <SelectItem value="NIT">NIT Empresa</SelectItem>
                                                            <SelectItem value="CE">Cédula Ext. (CE)</SelectItem>
                                                            <SelectItem value="PASAPORTE">Pasaporte</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <Label className="text-[11px] font-semibold text-gray-600">No. Documento / NIT (Opcional)</Label>
                                                    <Input
                                                        value={sicDocNumber}
                                                        onChange={e => setSicDocNumber(e.target.value)}
                                                        placeholder="Ej: 900123456"
                                                        className="h-8 text-xs bg-white rounded-lg"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-[11px] font-semibold text-gray-600">Año Radicación</Label>
                                                    <Input
                                                        value={sicYear}
                                                        onChange={e => setSicYear(e.target.value)}
                                                        placeholder="Ej: 2024"
                                                        className="h-8 text-xs bg-white rounded-lg font-mono"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="text-[11px] font-semibold text-gray-600">Delegatura SIC</Label>
                                                <Select value={sicDelegatura} onValueChange={setSicDelegatura}>
                                                    <SelectTrigger className="h-8 text-xs bg-white rounded-lg"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="proteccion_consumidor">Delegatura de Protección al Consumidor</SelectItem>
                                                        <SelectItem value="propiedad_industrial">Delegatura de Propiedad Industrial (Marcas/Patentes)</SelectItem>
                                                        <SelectItem value="competencia_desleal">Delegatura para Asuntos Jurisdiccionales / Competencia Desleal</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    )}

                                    {/* Campos Desagregados para SUPERSOCIEDADES */}
                                    {selectedEntity === "supersociedades" && (
                                        <div className="p-3.5 bg-gradient-to-br from-emerald-50/60 to-teal-50/40 border border-emerald-100 rounded-xl space-y-3 animate-fade-in">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-emerald-950 flex items-center gap-1.5">
                                                    <Building2 className="h-3.5 w-3.5 text-emerald-600" /> Parámetros Específicos Superintendencia de Sociedades
                                                </span>
                                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Supersociedades Oficial</Badge>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                                <div>
                                                    <Label className="text-[11px] font-semibold text-gray-600">NIT Sociedad Concursada (sin DV)</Label>
                                                    <Input
                                                        value={superNit}
                                                        onChange={e => setSuperNit(e.target.value)}
                                                        placeholder="Ej: 860000000"
                                                        className="h-8 text-xs bg-white rounded-lg"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-[11px] font-semibold text-gray-600">Razón Social de la Compañía</Label>
                                                    <Input
                                                        value={superRazonSocial}
                                                        onChange={e => setSuperRazonSocial(e.target.value)}
                                                        placeholder="Ej: Avianca S.A."
                                                        className="h-8 text-xs bg-white rounded-lg"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="text-[11px] font-semibold text-gray-600">Delegatura / Área Especial</Label>
                                                <Select value={superDelegatura} onValueChange={setSuperDelegatura}>
                                                    <SelectTrigger className="h-8 text-xs bg-white rounded-lg"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="insolvencia">Procedimientos de Insolvencia (Ley 1116 de 2006)</SelectItem>
                                                        <SelectItem value="mercantiles">Procedimientos Mercantiles (Resolución de Conflictos)</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    )}

                                    {/* Textarea para radicados */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-700">
                                            {selectedEntity === "sic"
                                                ? "Copia y pega los radicados de la SIC (uno por línea o separados por comas):"
                                                : selectedEntity === "supersociedades"
                                                ? "Copia y pega los radicados / expedientes de Supersociedades (uno por línea):"
                                                : "Copia y pega los radicados de la Rama Judicial (23 dígitos, uno por línea):"}
                                        </label>
                                        <Textarea
                                            placeholder={
                                                selectedEntity === "sic"
                                                    ? `23-123456\n24-004521\n22-987654`
                                                    : selectedEntity === "supersociedades"
                                                    ? `2024-01-098765\n2023-01-045120\n2022-01-012398`
                                                    : `11001333603420150XXXXXXX\n20011333300120260XXXXXX\n25000234100020230XXXXXX`
                                            }
                                            rows={5}
                                            value={pastedText}
                                            onChange={(e) => { setPastedText(e.target.value); setResult(null); setProgressState(null); }}
                                            className="font-mono text-xs p-3 rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 bg-white shadow-xs"
                                        />
                                        <div className="flex items-center justify-between text-xs text-gray-500 px-1 pt-1">
                                            <span>
                                                Radicados detectados: <strong className="text-indigo-600 font-bold">{detectedCount}</strong>
                                            </span>
                                            {selectedEntity === "rama" && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-400 font-semibold text-[11px]">Origen por defecto:</span>
                                                    <select
                                                        value={pastedFuente}
                                                        onChange={(e) => setPastedFuente(e.target.value)}
                                                        className="text-xs border border-gray-200 rounded-lg px-2.5 py-1 bg-white font-bold text-gray-700 shadow-xs"
                                                    >
                                                        <option value="SAMAI">SAMAI</option>
                                                        <option value="CPNU">CPNU</option>
                                                        <option value="TYBA">TYBA</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </TabsContent>

                                {/* TAB 2: SUBIR EXCEL / CSV CON PLANTILLAS POR ENTIDAD */}
                                <TabsContent value="file" className="space-y-5">
                                    {/* Paso 1: Descargar Plantilla */}
                                    <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-100 space-y-3">
                                        <div className="space-y-2">
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                                <div>
                                                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                                                        <Download className="h-4 w-4 text-indigo-600" /> 1. Descargar Plantilla Especializada
                                                    </h4>
                                                    <p className="text-xs text-gray-600 mt-0.5">
                                                        Selecciona la entidad para obtener la plantilla con columnas optimizadas y validadas:
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleDownloadTemplate("xlsx")}
                                                        disabled={isDownloading}
                                                        className="rounded-lg h-8 text-xs font-semibold bg-white border-indigo-200 text-indigo-700 hover:bg-indigo-50 shadow-xs"
                                                    >
                                                        {isDownloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5 text-emerald-600" />}
                                                        Excel (.xlsx)
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleDownloadTemplate("csv")}
                                                        disabled={isDownloading}
                                                        className="rounded-lg h-8 text-xs font-semibold bg-white border-gray-200 text-gray-700 hover:bg-gray-50 shadow-xs"
                                                    >
                                                        {isDownloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <File className="h-3.5 w-3.5 mr-1.5 text-blue-600" />}
                                                        CSV (.csv)
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Selector de plantilla */}
                                            <div className="flex flex-wrap gap-1.5 pt-1">
                                                <button
                                                    type="button"
                                                    onClick={() => setTemplateEntity("all")}
                                                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                                        templateEntity === "all" ? "bg-indigo-600 text-white shadow-xs" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    Todas las Entidades (Unificada)
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setTemplateEntity("rama")}
                                                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                                        templateEntity === "rama" ? "bg-blue-600 text-white shadow-xs" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    Rama Judicial (SAMAI/CPNU/TYBA)
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setTemplateEntity("sic")}
                                                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                                        templateEntity === "sic" ? "bg-indigo-600 text-white shadow-xs" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    SIC (Superindustria)
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setTemplateEntity("supersociedades")}
                                                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                                        templateEntity === "supersociedades" ? "bg-emerald-600 text-white shadow-xs" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    Supersociedades
                                                </button>
                                            </div>
                                        </div>

                                        {/* Columnas específicas */}
                                        <div className="text-[11px] text-gray-600 pt-1 border-t border-blue-100/60">
                                            {templateEntity === "sic" && (
                                                <div className="space-y-1.5">
                                                    <p>
                                                        <strong>Columnas SIC:</strong> <code className="bg-white px-1 py-0.5 rounded text-indigo-700 font-bold">numero_proceso *</code>, <code className="bg-white px-1 py-0.5 rounded text-indigo-700">anho</code>, <code className="bg-white px-1 py-0.5 rounded text-indigo-700">tipo_documento</code>, <code className="bg-white px-1 py-0.5 rounded text-indigo-700">nit_cedula</code>, <code className="bg-white px-1 py-0.5 rounded text-indigo-700">delegatura</code>, <code className="bg-white px-1 py-0.5 rounded text-indigo-700">despacho</code>.
                                                    </p>
                                                    <p className="text-amber-800 font-medium text-[10.5px]">
                                                        💡 <em>Nota:</em> Al validar los radicados de la SIC se abrirá una ventana emergente de navegación; solo asegúrate de que los datos estén bien digitados en la plantilla para que el sistema los capture automáticamente.
                                                    </p>
                                                </div>
                                            )}
                                            {templateEntity === "supersociedades" && (
                                                <p>
                                                    <strong>Columnas Supersociedades:</strong> <code className="bg-white px-1 py-0.5 rounded text-emerald-700 font-bold">numero_proceso *</code>, <code className="bg-white px-1 py-0.5 rounded text-emerald-700">nit_sociedad</code>, <code className="bg-white px-1 py-0.5 rounded text-emerald-700">razon_social</code>, <code className="bg-white px-1 py-0.5 rounded text-emerald-700">delegatura</code>, <code className="bg-white px-1 py-0.5 rounded text-emerald-700">despacho</code>.
                                                </p>
                                            )}
                                            {templateEntity === "rama" && (
                                                <p>
                                                    <strong>Columnas Rama Judicial:</strong> <code className="bg-white px-1 py-0.5 rounded text-blue-700 font-bold">numero_proceso *</code>, <code className="bg-white px-1 py-0.5 rounded text-blue-700">fuente (SAMAI/CPNU/TYBA)</code>, <code className="bg-white px-1 py-0.5 rounded text-blue-700">demandante</code>, <code className="bg-white px-1 py-0.5 rounded text-blue-700">demandado</code>, <code className="bg-white px-1 py-0.5 rounded text-blue-700">ubicacion</code>.
                                                </p>
                                            )}
                                            {templateEntity === "all" && (
                                                <p>
                                                    <strong>Columnas Unificadas:</strong> <code className="bg-white px-1 py-0.5 rounded text-indigo-700 font-bold">numero_proceso *</code>, <code className="bg-white px-1 py-0.5 rounded text-indigo-700">fuente</code>, <code className="bg-white px-1 py-0.5 rounded text-indigo-700">demandante</code>, <code className="bg-white px-1 py-0.5 rounded text-indigo-700">demandado</code>, <code className="bg-white px-1 py-0.5 rounded text-indigo-700">ubicacion</code>.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Paso 2: Seleccionar y Cargar Archivo */}
                                    <div className="space-y-3">
                                        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                                            <Upload className="h-4 w-4 text-indigo-600" /> 2. Seleccionar y Cargar Archivo (Máx. 1,000 registros)
                                        </h4>

                                        <div className="border-2 border-dashed border-gray-200 hover:border-indigo-400 transition-colors rounded-2xl p-6 text-center bg-gray-50/50 relative">
                                            <input
                                                type="file"
                                                accept=".xlsx, .xls, .csv"
                                                onChange={handleFileChange}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                            />
                                            <div className="flex flex-col items-center justify-center gap-2 pointer-events-none">
                                                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                                                    <Upload className="h-5 w-5" />
                                                </div>
                                                {file ? (
                                                    <div>
                                                        <p className="text-sm font-bold text-indigo-700">{file.name}</p>
                                                        <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB - Haga clic o arrastre para cambiar</p>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-700">Haga clic aquí o arrastre su archivo .xlsx o .csv</p>
                                                        <p className="text-xs text-gray-400">Soporta archivos Excel (.xlsx) y CSV codificados en UTF-8</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </div>

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button variant="outline" className="rounded-xl" onClick={() => setIsOpen(false)}>
                                Cerrar
                            </Button>
                            <Button
                                className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md shadow-indigo-200"
                                onClick={handleVerify}
                                disabled={(activeTab === "file" && !file) || (activeTab === "paste" && detectedCount === 0) || isVerifying}
                            >
                                {isVerifying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                                {isVerifying ? "Verificando procesos..." : "Verificar procesos"}
                            </Button>
                        </DialogFooter>
                    </>
                ) : step === "verifying" ? (
                    <>
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold flex items-center gap-2">
                                <Upload className="h-5 w-5 text-emerald-600" />
                                Consultando y Validando Expedientes
                            </DialogTitle>
                            <CardDescription>
                                Verificando radicados ante la Rama Judicial, SIC o Supersociedades en tiempo real
                            </CardDescription>
                        </DialogHeader>

                        <div className="py-8 space-y-6 text-center">
                            <div className="flex flex-col items-center justify-center">
                                <Loader2 className="h-12 w-12 text-emerald-600 animate-spin mb-3 stroke-[2.5]" />
                                <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">
                                    Buscando los procesos
                                </h3>
                                <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
                                    Consultando {verifyingList.length} radicados. Podrás revisarlos antes de registrarlos en tu vigilancia judicial.
                                </p>
                            </div>

                            <ScrollArea className="h-64 border border-gray-100 rounded-2xl bg-white p-2 text-left shadow-2xs">
                                <div className="divide-y divide-gray-100">
                                    {verifyingList.map((item, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                                                <span className="font-mono font-bold text-sm text-gray-800 tracking-tight">
                                                    {item.numero_proceso}
                                                </span>
                                            </div>
                                            <span className="text-xs font-semibold text-gray-500">
                                                {item.status === "found" ? "Proceso encontrado" : item.status === "error" ? "No encontrado" : "Consultando portal..."}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="space-y-4 py-2">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Revisa antes de registrar</h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    Verifica que las partes y la entidad correspondan a tus casos. Solo se registrarán los expedientes que dejes marcados.
                                </p>
                            </div>

                            <ScrollArea className="h-80 border border-gray-100 rounded-2xl bg-slate-50/50 p-3">
                                <div className="space-y-3">
                                    {verifiedList.map((proc, idx) => {
                                        const fuenteUpper = (proc.fuente || "").toUpperCase().trim();
                                        const cleanDigits = (proc.numero_proceso || "").replace(/\D/g, "");
                                        const isAdministrative =
                                            fuenteUpper === "SIC" ||
                                            fuenteUpper === "SUPERSOCIEDADES" ||
                                            fuenteUpper === "SUPERINTENDENCIA" ||
                                            (cleanDigits.length !== 23 && (proc.numero_proceso || "").includes("-"));

                                        return (
                                        <div
                                            key={idx}
                                            onClick={() => {
                                                const copy = [...verifiedList];
                                                copy[idx].selected = !copy[idx].selected;
                                                setVerifiedList(copy);
                                            }}
                                            className={`p-4 rounded-2xl border transition-all cursor-pointer bg-white shadow-2xs ${
                                                proc.selected ? "border-indigo-500 ring-2 ring-indigo-500/10" : "border-gray-200 opacity-60"
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <Checkbox
                                                    checked={proc.selected}
                                                    onCheckedChange={(val) => {
                                                        const copy = [...verifiedList];
                                                        copy[idx].selected = !!val;
                                                        setVerifiedList(copy);
                                                    }}
                                                    className="mt-1"
                                                />
                                                {isAdministrative ? (
                                                    /* ── FORMULARIO ESPECIALIZADO: SIC Y SUPERSOCIEDADES ── */
                                                    <div className="flex-1 space-y-1.5 min-w-0">
                                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                                            <span className="font-mono font-bold text-sm text-indigo-700 tracking-tight">
                                                                {proc.numero_proceso}
                                                            </span>
                                                            <div className="flex items-center gap-1.5">
                                                                <Badge className={
                                                                    fuenteUpper === "SIC"
                                                                        ? "bg-purple-100 text-purple-800 border-purple-200 text-[10px]"
                                                                        : "bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]"
                                                                }>
                                                                    {proc.fuente || "SIC"}
                                                                </Badge>
                                                                {proc.encontrado ? (
                                                                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                                                        Verificado en Portal
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                                                        Requiere ingresar partes
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {!proc.encontrado && (
                                                            <div className="bg-amber-50/70 border border-amber-200/80 rounded px-2.5 py-1.5 text-[11px] text-amber-900 flex flex-col gap-1.5 mt-1">
                                                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                                                    <span>
                                                                        El portal oficial de {proc.fuente || "la entidad"} exige verificación interactiva. Puedes completar las partes abajo o importar con 1 clic:
                                                                    </span>
                                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (fuenteUpper === "SIC") {
                                                                                    window.open("https://consultatramites.sic.gov.co/consulta-externa", "_blank");
                                                                                } else if (fuenteUpper === "SUPERSOCIEDADES") {
                                                                                    window.open("https://servicios.supersociedades.gov.co/barandavirtual/", "_blank");
                                                                                }
                                                                            }}
                                                                            className="h-6 text-[10px] bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50 shrink-0 font-medium"
                                                                        >
                                                                            <ExternalLink className="h-3 w-3 mr-1" />
                                                                            Abrir portal {proc.fuente || ""}
                                                                        </Button>
                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handlePasteSICData(idx);
                                                                            }}
                                                                            className="h-6 text-[10px] bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100 shrink-0 font-medium"
                                                                        >
                                                                            <ClipboardCopy className="h-3 w-3 mr-1" />
                                                                            Pegar datos de la web
                                                                        </Button>
                                                                        {fuenteUpper === "SIC" && (
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    copyBookmarklet();
                                                                                }}
                                                                                className="h-6 text-[10px] text-indigo-700 hover:bg-indigo-50 shrink-0 font-medium px-2"
                                                                            >
                                                                                <Bookmark className="h-3 w-3 mr-1 text-indigo-500" />
                                                                                Marcador 1-Clic
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-1">
                                                            <div className="space-y-0.5">
                                                                <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider flex items-center justify-between">
                                                                    <span>Demandante / Actor:</span>
                                                                    {(!proc.demandante || proc.demandante === "NO ESPECIFICADO") && (
                                                                        <span className="text-[9px] text-amber-600 font-normal">Editable</span>
                                                                    )}
                                                                </label>
                                                                <Input
                                                                    value={proc.demandante === "NO ESPECIFICADO" ? "" : proc.demandante}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setVerifiedList(prev => {
                                                                            const copy = [...prev];
                                                                            copy[idx] = { ...copy[idx], demandante: val };
                                                                            return copy;
                                                                        });
                                                                    }}
                                                                    placeholder="Nombre del demandante o actor..."
                                                                    className="h-7 text-xs bg-white border-slate-200 focus-visible:ring-1 focus-visible:ring-indigo-500 font-medium"
                                                                />
                                                            </div>
                                                            <div className="space-y-0.5">
                                                                <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider flex items-center justify-between">
                                                                    <span>Demandado / Opositor:</span>
                                                                    {(!proc.demandado || proc.demandado === "NO ESPECIFICADO") && (
                                                                        <span className="text-[9px] text-amber-600 font-normal">Editable</span>
                                                                    )}
                                                                </label>
                                                                <Input
                                                                    value={proc.demandado === "NO ESPECIFICADO" ? "" : proc.demandado}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setVerifiedList(prev => {
                                                                            const copy = [...prev];
                                                                            copy[idx] = { ...copy[idx], demandado: val };
                                                                            return copy;
                                                                        });
                                                                    }}
                                                                    placeholder="Nombre del demandado, empresa o entidad..."
                                                                    className="h-7 text-xs bg-white border-slate-200 focus-visible:ring-1 focus-visible:ring-indigo-500 font-medium"
                                                                />
                                                            </div>
                                                        </div>

                                                        <p className="text-xs text-gray-500 truncate" title={proc.ubicacion}>
                                                            <strong>Despacho / Delegatura:</strong> {proc.ubicacion || "NO ESPECIFICADO"}
                                                        </p>

                                                        {/* Metadatos adicionales SIC o Supersociedades */}
                                                        {(proc.delegatura || proc.nit_sociedad || proc.razon_social || proc.anho) && (
                                                            <div className="flex flex-wrap gap-1.5 text-[10px] pt-1">
                                                                {proc.delegatura && (
                                                                    <span className="bg-slate-100 px-2 py-0.5 rounded-md font-medium text-slate-700">
                                                                        <strong>Delegatura:</strong> {proc.delegatura}
                                                                    </span>
                                                                )}
                                                                {proc.nit_sociedad && (
                                                                    <span className="bg-slate-100 px-2 py-0.5 rounded-md font-medium text-slate-700">
                                                                        <strong>NIT Sociedad:</strong> {proc.nit_sociedad}
                                                                    </span>
                                                                )}
                                                                {proc.razon_social && (
                                                                    <span className="bg-slate-100 px-2 py-0.5 rounded-md font-medium text-slate-700">
                                                                        <strong>Razón Social:</strong> {proc.razon_social}
                                                                    </span>
                                                                )}
                                                                {proc.anho && (
                                                                    <span className="bg-slate-100 px-2 py-0.5 rounded-md font-medium text-slate-700">
                                                                        <strong>Año:</strong> {proc.anho}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    /* ── DISEÑO ORIGINAL Y ESTRUCTURADO: SAMAI, CPNU, TYBA (RAMA JUDICIAL) ── */
                                                    <div className="flex-1 space-y-1.5 min-w-0">
                                                        <div className="flex items-center justify-between flex-wrap gap-1.5">
                                                            <span className="font-mono font-extrabold text-sm text-gray-900 tracking-tight">
                                                                {proc.numero_proceso}
                                                            </span>
                                                            <div className="flex items-center gap-1.5">
                                                                {proc.tipo_proceso && (
                                                                    <Badge variant="outline" className="text-[10px] px-2 py-0.5 bg-slate-100 border-slate-200 text-slate-700 font-semibold truncate max-w-[200px]">
                                                                        {proc.tipo_proceso}
                                                                    </Badge>
                                                                )}
                                                                <Badge variant="outline" className={`text-[10px] px-2 py-0.5 font-bold uppercase ${
                                                                    fuenteUpper === "CPNU"
                                                                        ? "bg-purple-50 border-purple-200 text-purple-700"
                                                                        : fuenteUpper === "TYBA"
                                                                        ? "bg-rose-50 border-rose-200 text-rose-700"
                                                                        : "bg-indigo-50 border-indigo-200 text-indigo-700"
                                                                }`}>
                                                                    {proc.fuente || "SAMAI"}
                                                                </Badge>
                                                                {proc.encontrado ? (
                                                                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                                                        Verificado en Portal
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                                                        No encontrado
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="text-xs font-semibold text-gray-800 leading-snug">
                                                            Demandante: <span className="font-bold text-gray-900">{proc.demandante || "NO ESPECIFICADO"}</span> | DEMANDADO: <span className="font-bold text-gray-900">{proc.demandado || "NO ESPECIFICADO"}</span>
                                                        </p>
                                                        <p className="text-[11px] font-mono text-gray-500 uppercase">
                                                            {proc.ubicacion || "DESPACHO 000 - CONSEJO DE ESTADO - SECCIÓN PRIMERA - BOGOTÁ"}
                                                        </p>

                                                        {(proc.ponente || proc.subclase || proc.recurso || proc.fecha_radicado || proc.asunto) && (
                                                            <div className="mt-2 pt-2 border-t border-gray-100/90 space-y-1.5">
                                                                {proc.asunto && proc.asunto !== `Demandante: ${proc.demandante} | DEMANDADO: ${proc.demandado}` && (
                                                                    <p className="text-[11px] text-slate-600 bg-amber-50/60 p-2 rounded-lg border border-amber-100/80 line-clamp-2">
                                                                        <strong className="font-semibold text-amber-900">Asunto:</strong> {proc.asunto}
                                                                    </p>
                                                                )}
                                                                <div className="flex flex-wrap gap-1.5 text-[10px]">
                                                                    {proc.ponente && (
                                                                        <span className="bg-slate-100 px-2 py-0.5 rounded-md font-medium text-slate-700">
                                                                            <strong>Ponente:</strong> {proc.ponente}
                                                                        </span>
                                                                    )}
                                                                    {proc.subclase && (
                                                                        <span className="bg-slate-100 px-2 py-0.5 rounded-md font-medium text-slate-700">
                                                                            <strong>Subclase:</strong> {proc.subclase}
                                                                        </span>
                                                                    )}
                                                                    {proc.recurso && (
                                                                        <span className="bg-slate-100 px-2 py-0.5 rounded-md font-medium text-slate-700">
                                                                            <strong>Recurso:</strong> {proc.recurso}
                                                                        </span>
                                                                    )}
                                                                    {proc.fecha_radicado && (
                                                                        <span className="bg-slate-100 px-2 py-0.5 rounded-md font-medium text-slate-700">
                                                                            <strong>Radicado:</strong> {proc.fecha_radicado}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            </ScrollArea>

                            {/* Barra de Progreso en Tiempo Real vía WebSocket */}
                            {progressState && (
                                <div className={`p-4 rounded-xl border space-y-3 animate-fade-in shadow-xs ${
                                    progressState.percentage === 100 && !isUploading
                                        ? "bg-emerald-50/90 border-emerald-200/80"
                                        : "bg-gradient-to-r from-indigo-50/90 to-purple-50/90 border-indigo-200/80"
                                }`}>
                                    <div className="flex items-center justify-between text-xs font-bold text-gray-900">
                                        <span className="flex items-center gap-2">
                                            {progressState.percentage === 100 && !isUploading ? (
                                                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                                            ) : (
                                                <Loader2 className="h-4 w-4 text-indigo-600 animate-spin shrink-0" />
                                            )}
                                            <span>
                                                {progressState.percentage === 100 && !isUploading
                                                    ? `Ingesta masiva completada exitosamente (${progressState.current} de ${progressState.total})`
                                                    : `Procesando ingesta masiva en tiempo real (${progressState.current} de ${progressState.total})`}
                                            </span>
                                        </span>
                                        <span className={`font-mono font-extrabold text-sm px-2.5 py-0.5 rounded-full border shadow-2xs ${
                                            progressState.percentage === 100 && !isUploading
                                                ? "bg-white text-emerald-700 border-emerald-200"
                                                : "bg-white text-indigo-700 border-indigo-200"
                                        }`}>
                                            {progressState.percentage}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-white/80 rounded-full h-2.5 overflow-hidden shadow-inner border border-gray-100">
                                        <div
                                            className={`h-2.5 transition-all duration-300 rounded-full ${
                                                progressState.percentage === 100 && !isUploading
                                                    ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                                                    : "bg-gradient-to-r from-indigo-500 to-purple-500"
                                            }`}
                                            style={{ width: `${progressState.percentage}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Resultados de la Ingesta */}
                            {result && (
                                <div className="p-4 rounded-xl border border-gray-100 bg-white space-y-3 animate-fade-in shadow-xs">
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                                            <p className="text-[10px] font-bold text-gray-500 uppercase">Total Procesados</p>
                                            <p className="text-lg font-black text-gray-800">{result.total_procesados || 0}</p>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-emerald-50 border border-emerald-100">
                                            <p className="text-[10px] font-bold text-emerald-600 uppercase">Insertados OK</p>
                                            <p className="text-lg font-black text-emerald-700">{result.total_insertados || 0}</p>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-100">
                                            <p className="text-[10px] font-bold text-amber-600 uppercase">Omitidos / Error</p>
                                            <p className="text-lg font-black text-amber-700">{result.total_omitidos || 0}</p>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-indigo-50 border border-indigo-100">
                                            <p className="text-[10px] font-bold text-indigo-600 uppercase">Job ID</p>
                                            <p className="text-xs font-mono font-bold text-indigo-700 truncate" title={result.job_id || ""}>{result.job_id || "N/A"}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button variant="outline" className="rounded-xl" onClick={() => { setStep("input"); setResult(null); setProgressState(null); }}>
                                Volver a editar
                            </Button>
                            {result ? (
                                <Button className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" onClick={() => setIsOpen(false)}>
                                    Finalizar y Cerrar
                                </Button>
                            ) : (
                                <Button
                                    className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md shadow-indigo-200"
                                    onClick={handleUpload}
                                    disabled={selectedCount === 0 || isUploading}
                                >
                                    {isUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                    {isUploading ? "Registrando..." : `Registrar ${selectedCount} proceso${selectedCount === 1 ? "" : "s"}`}
                                </Button>
                            )}
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
